import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { debounce } from 'lodash/fp';
import classNames from 'classnames';
import { useFeatureFlags, useSessionStorage } from 'hooks';
import AppSwitcher from 'components/shared/AppSwitcher';
import CogMenu from 'components/CogMenu';
import GradePendingNotice from 'components/GradePending/GradePendingNotice';
import ToggleLessonNav from 'components/lesson/LessonHeader/ToggleLessonNav';
import AssignModal from 'components/AssignModal';
import { routes } from 'constants/config';
import { CheckSessionIFrame } from 'oidc-client-ts';
import NavbarMenu from './NavbarMenu';
import ImitatingAlert from './ImitatingAlert';

const Navbar = ({
  imitating,
  showGradePending,
  currentPath,
  companyLogoUrl,
  navLogoLink,

  meta,

  // CogMenu
  anonUniversityLoginUrl,
}) => {
  const { features, locale, currentUser, currentCompany, centralAuth } = meta;
  const {
    canViewTrainingEvents,
    canCreateAssignments,
    canViewLearningContents,
    canViewUsers,
    canViewGradebook,
    canViewGrades,
    canViewInsights,
    canViewSkills,
  } = meta.permissions;
  const containerRef = useRef(undefined);
  const [forceCollapse, setForceCollapse] = useState(false);
  const [collapsedWidth, setCollapsedWidth] = useState(false);
  const isIframedInSeismic = useSessionStorage('iframedInSeismic');
  const [checkSessionIframe, setCheckSessionIframe] = useState();
  const hasUnificationFeatureFlag = useFeatureFlags(
    'readiness-enable-unification',
  );

  useEffect(() => {
    if (!checkSessionIframe && centralAuth && centralAuth.sessionState) {
      const centralAuthSessionChangedCallback = () => {
        window.location.href = centralAuth.loggedOutPath;
      };

      const sessionIframe = new CheckSessionIFrame(
        centralAuthSessionChangedCallback,
        centralAuth.clientId,
        centralAuth.checkSessionUrl,
        5, // every 5 seconds
      );
      setCheckSessionIframe(sessionIframe);

      sessionIframe.load();
      sessionIframe.start(centralAuth.sessionState);
    }

    return () => {
      if (checkSessionIframe) {
        checkSessionIframe.stop();
      }
    };
  }, [checkSessionIframe, centralAuth]);

  // Force the navbar to collapse if the navbar height expands prior to the breakpoint defined in styling
  useLayoutEffect(() => {
    const stableContainerRef = containerRef?.current;

    const checkForCollapse = () => {
      // If previously collapsed, check if we should un-collapsed
      if (window.innerWidth >= 1200) {
        // Un-collapse if screen width is larger than previous breakpoint or is massively large
        if (
          forceCollapse &&
          (window.innerWidth > collapsedWidth || window.innerWidth >= 3500)
        ) {
          setForceCollapse(false);
        }
      } else if (!forceCollapse) {
        setForceCollapse(true);
      }
    };

    // The ResizeObserver handles pixel height slightly more accurately at small zoom levels
    const navbarObserver = new ResizeObserver(
      debounce(200, ([navbarElement]) => {
        if (!forceCollapse && navbarElement.contentRect.height > 56) {
          setCollapsedWidth(window.innerWidth);
          setForceCollapse(true);
        }
      }),
    );

    navbarObserver.observe(stableContainerRef);
    window.addEventListener('resize', checkForCollapse);

    // The ResizeObserver runs on init, but the window resize listener does not
    // so we want to manually run the handler when this component is mounted.
    checkForCollapse();

    return () => {
      window.removeEventListener('resize', checkForCollapse);
      navbarObserver.unobserve(stableContainerRef);
    };
  }, [forceCollapse, collapsedWidth]);
  const [isScormSection, setIsScormSection] = useState(false);
  const hasUnificationFlag = useFeatureFlags(['readiness-enable-unification']);
  const [hideNavigation, setHideNavigation] = useState(false);
  const [hideLessonHeader, setHideLessonHeader] = useState(false);

  const onToggleNavButtonClick = () => {
    pubsub.publish('hideLessonHeader', !hideLessonHeader);
    setHideLessonHeader(!hideLessonHeader);
  };

  useEffect(() => {
    if (hasUnificationFlag) {
      pubsub.subscribe('hideNav', shouldHide => {
        setHideNavigation(shouldHide);
      });
    } else {
      pubsub.subscribe('showNonUnifiedScormToggle', showToggle => {
        setIsScormSection(showToggle);
      });
    }
    return () => {
      if (hasUnificationFlag) {
        pubsub.unsubscribe('hideNav', shouldHide =>
          setHideNavigation(shouldHide),
        );
      } else {
        pubsub.unsubscribe('showNonUnifiedScormToggle', showToggle => {
          setIsScormSection(showToggle);
        });
      }
    };
  }, [hasUnificationFlag]);

  const isActiveFuzzyMatch = path =>
    currentPath === path || currentPath.startsWith(path + '/');

  if (hideNavigation) return null;

  return (
    <>
      {isIframedInSeismic && hasUnificationFeatureFlag && (
        <h1 className="iFrame-page-heading">{t('common.training')}</h1>
      )}
      <nav
        ref={containerRef}
        className={classNames('navbar navbar-default', {
          collapsed: forceCollapse,
          iFrame: isIframedInSeismic && hasUnificationFeatureFlag,
        })}
        role="navigation"
      >
        {imitating && (
          <ImitatingAlert
            currentUser={{
              ...currentUser,
              ...meta.userMeta,
            }}
            currentCompany={currentCompany}
          />
        )}

        <div className="container-fluid trk_container_readiness_main-nav">
          <div className="navbar-header Header">
            <div className="d-f fd-r ai-c">
              {!forceCollapse && (
                <div id="appSwitcher">
                  {features.skills && <AppSwitcher locale={locale} />}
                </div>
              )}
              {!isIframedInSeismic && (
                <div className="logo-container">
                  <a href={navLogoLink}>
                    <img
                      className="logo"
                      src={companyLogoUrl}
                      alt={t('common.application_name')}
                    />
                  </a>
                </div>
              )}
            </div>
            <div
              className={classNames(
                'navbar-toggle-container',
                locale.direction === 'rtl' ? 'pull-left' : 'pull-right',
              )}
            >
              <button
                type="button"
                className="navbar-toggle"
                data-toggle="collapse"
                data-target="#lly-navbar-links"
              >
                <span className="sr-only">Toggle navigation</span>
                <span className="icon-bar" />
                <span className="icon-bar" />
                <span className="icon-bar" />
              </button>
            </div>
          </div>

          {canCreateAssignments && (
            <div id="AssignModal-react-component">
              <AssignModal state={{ meta }} timeZones={meta.timeZones} />
            </div>
          )}
          {isScormSection && !hasUnificationFlag && (
            <ToggleLessonNav
              className="mt-10"
              isOpen={hideLessonHeader}
              setIsOpen={onToggleNavButtonClick}
              showPopover={meta.userMeta.canSeeLessonDetailsHiddenPopover}
              canCreateAssignments={canCreateAssignments}
            />
          )}
          <div className="collapse navbar-collapse" id="lly-navbar-links">
            <NavbarMenu
              navbarCollapsed={forceCollapse}
              currentCompany={currentCompany}
              currentPath={currentPath}
              linkedUsers={meta.userMeta.linkedUsers}
              hasSkills={meta.features.skills}
              navLinks={[
                canViewLearningContents && {
                  path: routes.CONTENT.INDEX,
                  label: t('content.title'),
                  isActive: isActiveFuzzyMatch,
                  pendoClassName: 'trk_button_readiness_content-main-nav',
                },
                canViewTrainingEvents && {
                  path: routes.IN_PERSON_TRAINING.EVENTS,
                  label: t('training_events.events'),
                  pendoClassName: 'trk_button_readiness_events-main-nav',
                },
                canViewUsers && {
                  path: routes.PEOPLE,
                  label: t('people.title'),
                  pendoClassName: 'trk_button_readiness_people-main-nav',
                },
                canViewGradebook && {
                  path: routes.GRADEBOOK,
                  params: '?nav_link=true',
                  label: t('gradebook.title'),
                  pendoClassName: 'trk_button_readiness_gradebook-main-nav',
                },
                canViewInsights && {
                  path: features.embedded_insights_page
                    ? routes.INSIGHTS.V2
                    : routes.INSIGHTS.INDEX,
                  isActive: () =>
                    currentPath === routes.INSIGHTS.V2 ||
                    currentPath.startsWith(routes.INSIGHTS.V2) ||
                    currentPath === routes.INSIGHTS.INDEX ||
                    currentPath.startsWith(routes.INSIGHTS.INDEX),
                  label: t('insights.title'),
                  pendoClassName: 'trk_button_readiness_insights-main-nav',
                },
                {
                  path: routes.LEARN,
                  label: t('learn.title'),
                  isActive: isActiveFuzzyMatch,
                  pendoClassName: 'trk_button_readiness_learn-main-nav',
                },
                canViewSkills && {
                  path: routes.SKILLS.COACHING_INDEX,
                  label: t('skills.title'),
                  isActive: isActiveFuzzyMatch,
                },
              ]}
            />

            <ul className="nav navbar-nav navbar-right">
              {showGradePending && canViewGrades && <GradePendingNotice />}
              <CogMenu
                anonUniversityLoginUrl={anonUniversityLoginUrl}
                meta={meta}
                navbarCollapsed={forceCollapse}
                pendoClassName="trk_button_readiness_settings-icon"
              />
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default props => <Navbar {...props} />;
